import { ICustomFields } from 'modals/TimeOffModal/TimeOffModal.constants';
import { useReleaseShiftViewContext } from 'modals/ShiftModal/src/views/ReleaseShiftView/ReleaseShiftView.context';
import { createTimeOffPayloadTransformer } from 'modals/TimeOffModal/views/CreateTimeOffView/CreateTimeOffView.constants';
import { useMemo } from 'react';
import { ReleaseWithTimeOffPayloadType } from 'requests/index.types';

type UseReleaseShiftTimeOffDataArgs = {
  mainJaneId?: string;
  mainJanePrimaryCommunityId?: string;
  shouldValidate: boolean;
};

const RESERVED_TIME_OFF_KEYS = new Set([
  'dontAddTimeOff',
  'notes',
  'title',
  'pay_code_id',
  'allDay',
  'startDate',
  'startTime',
  'endDate',
  'endTime',
  'reason_category',
  'reason_message',
  'recurring',
  'required_skills',
  'allow_overtime',
]);

export const useReleaseShiftTimeOffData = ({
  mainJaneId,
  mainJanePrimaryCommunityId,
  shouldValidate,
}: UseReleaseShiftTimeOffDataArgs) => {
  const { values, hasFieldErrors, timeOffCustomFields, timeOffTimeZone } =
    useReleaseShiftViewContext();

  const dontAddTimeOff = values.dontAddTimeOff;
  const shouldValidateAndInclude = shouldValidate && !dontAddTimeOff && !!mainJaneId;

  const timeOffPayload = useMemo<ReleaseWithTimeOffPayloadType | undefined>(() => {
    if (!shouldValidateAndInclude) return undefined;

    const {
      dontAddTimeOff,
      allDay,
      startDate,
      startTime,
      endDate,
      endTime,
      title,
      pay_code_id,
      notes,
      ...rest
    } = values;

    const customFieldsValues = Object.keys(rest).reduce((acc, key) => {
      if (RESERVED_TIME_OFF_KEYS.has(key)) return acc;
      const value = rest[key];
      acc[key] = Array.isArray(value) ? value[0] || '' : (value as string);
      return acc;
    }, {} as ICustomFields);

    const transformed = createTimeOffPayloadTransformer({
      jane_id: mainJaneId!,
      allDay: allDay as boolean,
      startDate: startDate as string[],
      endDate: endDate as string[],
      startTime: startTime as string[],
      endTime: endTime as string[],
      title: title as string[],
      notes: (notes as string) || '',
      selectedFacilityId: Number(mainJanePrimaryCommunityId),
      customFields: customFieldsValues,
      timeOffCustomFields,
      timeZone: timeOffTimeZone,
      payCodeId: pay_code_id?.length ? Number(pay_code_id[0]) : undefined,
    });

    const releasePayload: ReleaseWithTimeOffPayloadType = {
      start_date_time: transformed.time_off.start_date_time,
      end_date_time: transformed.time_off.end_date_time,
      title: transformed.time_off.title,
      notes: transformed.time_off.notes,
      custom_fields: transformed.time_off.custom_fields?.length
        ? transformed.time_off.custom_fields
        : undefined,
      ...(transformed.time_off.pay_code_id !== undefined && {
        pay_code_id: transformed.time_off.pay_code_id,
      }),
    };

    return releasePayload;
  }, [
    shouldValidateAndInclude,
    mainJaneId,
    mainJanePrimaryCommunityId,
    values,
    timeOffCustomFields,
    timeOffTimeZone,
  ]);

  return {
    timeOffValidationErrors: undefined,
    timeOffValidationPending: false,
    timeOffPayload: {
      shouldInclude: shouldValidateAndInclude,
      hasErrors: hasFieldErrors,
      payload: shouldValidateAndInclude ? timeOffPayload : undefined,
    },
  };
};
